import React from "react";
import { Plus, Trash } from "@phosphor-icons/react";

const BOX = "w-full border-2 border-black bg-white px-2 py-1 text-sm";

function FormatSelect({ spec, value, defValue, onChange }) {
  const label = (v) => spec.options.find((o) => o.value === v)?.label;
  return (
    <label className="block text-[11px]">
      <span className="label-mono">{spec.label}</span>
      <select value={value ?? (defValue === undefined ? spec.options[0].value : "")} onChange={(e) => onChange(e.target.value)}
              className={`${BOX} mt-1`} data-testid={`lead-share-format-${spec.key}`}>
        {defValue !== undefined && <option value="">Default list: {label(defValue)}</option>}
        {spec.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function Exceptions({ cat, rows, inherited, onChange }) {
  const types = cat.field_types;
  const fields = [...cat.always_fields, ...cat.share_groups.flatMap((g) => g.fields)].filter((f) => types[f.key]);
  const fieldLabel = (k) => fields.find((f) => f.key === k)?.label || k;
  const opts = (k) => cat.formats.find((s) => s.key === types[k]).options;
  const styleLabel = (k, v) => opts(k).find((o) => o.value === v)?.label || v;
  const keys = Object.keys(rows);
  const add = () => {
    const k = fields.find((f) => !keys.includes(f.key))?.key;
    if (k) onChange({ ...rows, [k]: opts(k)[1].value });
  };
  const swap = (from, to) => onChange(Object.fromEntries(Object.entries(rows).map(([k, v]) => (k === from ? [to, opts(to)[1].value] : [k, v]))));
  const drop = (k) => onChange(Object.fromEntries(Object.entries(rows).filter(([x]) => x !== k)));
  return (
    <div className="mt-3 border-t border-zinc-300 pt-2">
      <div className="mb-1 flex items-center gap-2">
        <span className="label-mono">Different format for a single field</span>
        <button type="button" onClick={add} data-testid="lead-share-format-exception-add"
                className="ml-auto inline-flex items-center gap-1 border-2 border-black bg-white px-2 py-1 text-[10px] uppercase tracking-widest transition-colors duration-100 hover:bg-black hover:text-white">
          <Plus size={11} weight="bold" /> Add a field
        </button>
      </div>
      {Object.entries(inherited).map(([k, v]) => (
        <div key={k} className="text-[11px] text-zinc-600" data-testid={`lead-share-format-exception-default-${k}`}>
          From the default list: <span className={k in rows ? "line-through" : ""}>{fieldLabel(k)} → {styleLabel(k, v)}</span>
          {k in rows && <b className="ml-1">(changed for this buyer below)</b>}
        </div>
      ))}
      {keys.map((k) => (
        <div key={k} className="mt-1 grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]" data-testid={`lead-share-format-exception-${k}`}>
          <select value={k} onChange={(e) => swap(k, e.target.value)} className={BOX} data-testid={`lead-share-format-exception-field-${k}`}>
            {fields.filter((f) => f.key === k || !keys.includes(f.key)).map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
          <select value={rows[k]} onChange={(e) => onChange({ ...rows, [k]: e.target.value })} className={BOX} data-testid={`lead-share-format-exception-style-${k}`}>
            {opts(k).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button type="button" onClick={() => drop(k)} aria-label="Remove this field" data-testid={`lead-share-format-exception-remove-${k}`}
                  className="justify-self-start border-2 border-black bg-white px-2 py-1 transition-colors duration-100 hover:bg-red-600 hover:text-white">
            <Trash size={12} />
          </button>
        </div>
      ))}
    </div>
  );
}

// How answers are styled for this buyer's system. In a buyer window, blank = use the default list.
export function AnswerFormats({ cat, formats, fieldFormats, def, onFormats, onFieldFormats }) {
  const builtin = Object.fromEntries(cat.formats.map((s) => [s.key, s.options[0].value]));
  const defEff = def ? { ...builtin, ...def.formats } : null;
  const set = (k, v) => {
    const next = { ...formats };
    if (v === "") delete next[k]; else next[k] = v;
    onFormats(next);
  };
  return (
    <div className="mb-4 border-2 border-black bg-sky-50 p-3" data-testid="lead-share-formats">
      <div className="label-mono mb-1">How answers are sent</div>
      <p className="mb-2 text-[11px] text-zinc-600">
        Pick the style their system expects. Used in web posts and file drops, and in CRMs too if you choose so below. Lead emails always show Yes/No and readable dates.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {cat.formats.map((s) => (
          <FormatSelect key={s.key} spec={s} value={formats[s.key]} defValue={defEff ? defEff[s.key] : undefined} onChange={(v) => set(s.key, v)} />
        ))}
      </div>
      <Exceptions cat={cat} rows={fieldFormats} inherited={def?.field_formats || {}} onChange={onFieldFormats} />
    </div>
  );
}
